/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Activity, Save, RefreshCw, CloudOff, Cloud, Loader2, HeartPulse } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { saveVitalsOffline, listVitalsOffline, setMeta, getMeta } from '@/lib/offlineDb';
import { syncEngine } from '@/lib/syncEngine';
import { useOfflineSync } from '@/hooks/useOfflineSync';

/**
 * VitalsPanel — Parametri vitali OSS, LOCAL-FIRST.
 *
 *  • Il salvataggio scrive SEMPRE prima in IndexedDB (mai un errore per assenza di rete).
 *  • L'operazione viene accodata nell'outbox; il `syncEngine` ascolta l'evento `online`
 *    e spinge i dati al DB centrale, poi elimina la copia locale (purge_local).
 *  • Lo schema campi è cache-ato in locale per funzionare anche al primo avvio offline.
 */

const SCHEMA_KEY = 'vitals_schema_v1';
const FALLBACK_FIELDS = [
  { key: 'pa_sys', label: 'PA sistolica', unit: 'mmHg', min: 50, max: 260 },
  { key: 'pa_dia', label: 'PA diastolica', unit: 'mmHg', min: 30, max: 160 },
  { key: 'hr', label: 'Frequenza cardiaca', unit: 'bpm', min: 20, max: 250 },
  { key: 'temp', label: 'Temperatura', unit: '°C', min: 30, max: 45 },
  { key: 'spo2', label: 'SpO2', unit: '%', min: 50, max: 100 },
  { key: 'glucose', label: 'Glicemia', unit: 'mg/dL', min: 20, max: 700 },
];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export const VitalsPanel = ({ patient }) => {
  const net = useOfflineSync();
  const [fields, setFields] = useState(FALLBACK_FIELDS);
  const [values, setValues] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSchema = useCallback(async () => {
    try {
      const r = await api.get('/voice-diary/vitals/schema');
      if (r.data?.fields?.length) { setFields(r.data.fields); setMeta(SCHEMA_KEY, r.data.fields).catch(() => {}); }
    } catch {
      const cached = await getMeta(SCHEMA_KEY).catch(() => null);
      if (cached?.length) setFields(cached);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [remote, local] = await Promise.all([
        api.get('/voice-diary/vitals', { params: { limit: 10 } }).then((r) => r.data.items || []).catch(() => []),
        listVitalsOffline().catch(() => []),
      ]);
      const pending = local.filter((l) => l.sync_status !== 'synced').map((l) => ({ ...l, _local: true }));
      setHistory([...pending, ...remote]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSchema(); loadHistory(); }, [loadSchema, loadHistory]);
  // Al ritorno della rete il syncEngine svuota l'outbox: ricarichiamo la cronologia (copie locali eliminate).
  useEffect(() => { if (net.online && !net.syncing && net.lastSyncAt) loadHistory(); }, [net.online, net.syncing, net.lastSyncAt, loadHistory]);

  const filled = fields.filter((f) => values[f.key] !== undefined && values[f.key] !== '');
  const invalid = filled.filter((f) => { const v = Number(values[f.key]); return Number.isNaN(v) || v < f.min || v > f.max; });

  const save = async () => {
    if (filled.length === 0) { toast.error('Inserisci almeno un parametro'); return; }
    if (invalid.length) { toast.error(`Valore non plausibile: ${invalid.map((f) => f.label).join(', ')}`); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(filled.map((f) => [f.key, Number(values[f.key])]));
      await saveVitalsOffline({ patient_id: patient?.id || null, patient_label: patient?.label?.trim() || null, values: payload, note: note.trim() || null });
      setValues({}); setNote('');
      toast.success(navigator.onLine ? 'Parametri salvati · sincronizzazione in corso' : 'Parametri salvati offline · verranno inviati al ritorno della rete');
      await loadHistory();
      syncEngine.flush().then(loadHistory);
    } catch (e) {
      toast.error(e?.message || 'Salvataggio locale non riuscito');
    } finally { setSaving(false); }
  };

  return (
    <Card className="rounded-xl" data-testid="vitals-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Activity className="h-4 w-4" /></div>
            <div>
              <h2 className="font-display font-semibold">Parametri vitali</h2>
              <p className="text-xs text-muted-foreground">Rilevazione a domicilio · salvataggio locale immediato, invio al DB centrale appena c'è rete.</p>
            </div>
          </div>
          <Badge variant="outline" className={cn('text-[10px] gap-1', net.online ? 'border-success/50 text-success' : 'border-warning/60 text-warning')} data-testid="vitals-net-badge">
            {net.online ? <Cloud className="h-3 w-3" /> : <CloudOff className="h-3 w-3" />} {net.online ? (net.syncing ? 'sincronizzazione…' : 'online') : 'offline · coda locale'}{net.pending > 0 && ` · ${net.pending} in coda`}
          </Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vitals-form">
          {fields.map((f) => {
            const v = values[f.key] ?? '';
            const bad = v !== '' && (Number.isNaN(Number(v)) || Number(v) < f.min || Number(v) > f.max);
            return (
              <div key={f.key}>
                <Label htmlFor={`vital-${f.key}`} className="text-xs">{f.label} <span className="text-muted-foreground">({f.unit})</span></Label>
                <Input id={`vital-${f.key}`} type="number" inputMode="decimal" step="0.1" value={v} onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))} placeholder={`${f.min}–${f.max}`} aria-invalid={bad} className={cn('h-11 rounded-xl', bad && 'border-destructive focus-visible:ring-destructive')} data-testid={`vitals-input-${f.key}`} />
              </div>
            );
          })}
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
          <div>
            <Label htmlFor="vitals-note" className="text-xs">Nota (facoltativa)</Label>
            <Input id="vitals-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} placeholder="es. paziente a riposo, dopo colazione" className="h-11 rounded-xl" data-testid="vitals-note" />
          </div>
          <Button onClick={save} disabled={saving || filled.length === 0} className="h-11 rounded-xl min-w-[170px]" data-testid="vitals-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {net.online ? 'Salva parametri' : 'Salva offline'}
          </Button>
        </div>
        {patient?.label && <p className="text-[11px] text-muted-foreground" data-testid="vitals-patient-hint">Paziente: <b>{patient.label}</b> (dal selettore della nota)</p>}

        <div className="pt-3 border-t border-border/70">
          <div className="flex items-center gap-2 mb-2">
            <HeartPulse className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Ultime rilevazioni</span>
            <Button variant="ghost" size="sm" onClick={loadHistory} disabled={loading} className="ml-auto h-8" data-testid="vitals-reload-btn"><RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /></Button>
          </div>
          {loading ? <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-10 rounded-xl" />)}</div>
            : history.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="vitals-empty">Nessuna rilevazione registrata.</p>
              : (
                <ul className="space-y-1.5" data-testid="vitals-history">
                  {history.map((h) => (
                    <li key={h.id} className={cn('rounded-xl border px-3 py-2 text-xs flex items-center gap-2 flex-wrap', h._local ? 'border-warning/50 bg-warning/5' : 'border-border/70')} data-testid={`vitals-row-${h.id}`}>
                      <span className="text-muted-foreground">{fmtTime(h.measured_at)}</span>
                      {h.patient_label && <span className="font-medium">{h.patient_label}</span>}
                      <span className="flex-1 font-mono">{Object.entries(h.values || {}).map(([k, v]) => { const f = fields.find((x) => x.key === k); return `${f?.label?.split(' ')[0] || k} ${v}${f?.unit ? ` ${f.unit}` : ''}`; }).join(' · ')}</span>
                      {h._local ? <Badge variant="outline" className="text-[9px] border-warning/50 text-warning">in attesa di sync</Badge> : <Badge variant="outline" className="text-[9px] border-success/40 text-success">sincronizzato</Badge>}
                    </li>
                  ))}
                </ul>
              )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VitalsPanel;
